import { useEffect, useRef, useState } from 'react'
import { FileText, Plus, Search } from 'lucide-react'

import type { Invoice } from '@/shared/domain'
import { Button } from '@/shared/ui/primitives'
import { useIsDesktop } from '@/shared/ui/hooks/use-is-desktop'
import { DataTableSkeleton } from './data-table-skeleton'
import { EmptyState } from './empty-state'
import { ErrorState } from './error-state'
import type { PaymentRowActionHandlers } from './payment-row'
import { PaymentRegisterMobileList } from './payment-register-mobile-list'
import {
  PaymentRegisterTable,
  type PaymentSortColumn,
  type PaymentSortableColumn,
  type PaymentSortOrder,
} from './payment-register-table'

export type PaymentRegisterListProps = {
  invoices: Invoice[]
  isLoading: boolean
  isError: boolean
  isFiltered: boolean
  onRetry: () => void
  onCreateInvoice: () => void
  sortBy: PaymentSortColumn
  sortOrder: PaymentSortOrder
  onSortChange: (column: PaymentSortableColumn) => void
  highlightedId?: string | null
  actions: PaymentRowActionHandlers
}

export function PaymentRegisterList({
  invoices,
  isLoading,
  isError,
  isFiltered,
  onRetry,
  onCreateInvoice,
  sortBy,
  sortOrder,
  onSortChange,
  highlightedId,
  actions,
}: PaymentRegisterListProps) {
  const isDesktop = useIsDesktop()
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())
  const openedHighlightRef = useRef<string | null>(null)

  useEffect(() => {
    if (!highlightedId) return
    if (openedHighlightRef.current === highlightedId) return
    openedHighlightRef.current = highlightedId
    setExpandedIds((prev) => new Set(prev).add(highlightedId))
  }, [highlightedId])

  const handleToggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  if (isLoading) {
    return <DataTableSkeleton rows={8} columns={8} />
  }

  if (isError) {
    return (
      <ErrorState
        title="Failed to load invoices"
        description="Something went wrong while loading invoices."
        onRetry={onRetry}
      />
    )
  }

  if (invoices.length === 0) {
    if (isFiltered) {
      return (
        <EmptyState
          icon={Search}
          heading="No payments match these filters"
          description="Try clearing filters to see more results."
        />
      )
    }
    return (
      <EmptyState
        icon={FileText}
        heading="No payments yet"
        description="Create your first invoice to start tracking revenue."
        action={
          <Button onClick={onCreateInvoice}>
            <Plus className="size-4" />
            Create Invoice
          </Button>
        }
      />
    )
  }

  if (!isDesktop) {
    return (
      <PaymentRegisterMobileList
        invoices={invoices}
        expandedIds={expandedIds}
        onToggleExpand={handleToggleExpand}
        highlightedId={highlightedId}
        actions={actions}
      />
    )
  }

  return (
    <PaymentRegisterTable
      invoices={invoices}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortChange={onSortChange}
      expandedIds={expandedIds}
      onToggleExpand={handleToggleExpand}
      highlightedId={highlightedId}
      actions={actions}
    />
  )
}
